import { useCallback, useEffect, useMemo, useState } from "react";

import { RichTextEditor } from "@/components/rich-text-editor";
import { formatTimeAgo, useAutosave } from "@/lib/hooks/use-autosave";
import { useSiteContent, useUpdateSiteContent } from "@/lib/queries/use-site-content";
import { cn } from "@/lib/utils";

type PageTab = "home" | "writing" | "projects" | "about";

const PAGE_TABS: { id: PageTab; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "writing", label: "Writing" },
  { id: "projects", label: "Projects" },
  { id: "about", label: "About" },
];

export function AdminContent() {
  const { data: content, isLoading } = useSiteContent();
  const updateContent = useUpdateSiteContent();

  const [activeTab, setActiveTab] = useState<PageTab>("home");

  // Form state for all pages
  const [homeTagline, setHomeTagline] = useState("");
  const [homeDescription, setHomeDescription] = useState("");
  const [homeEducationLine, setHomeEducationLine] = useState("");

  const [writingTitle, setWritingTitle] = useState("");
  const [writingDescription, setWritingDescription] = useState("");

  const [projectsTitle, setProjectsTitle] = useState("");
  const [projectsDescription, setProjectsDescription] = useState("");

  const [aboutTitle, setAboutTitle] = useState("");
  const [aboutBio, setAboutBio] = useState("");

  // Load content when data is available
  useEffect(() => {
    if (content) {
      setHomeTagline(content.home_tagline || "");
      setHomeDescription(content.home_description || "");
      setHomeEducationLine(content.home_education_line || "");
      setWritingTitle(content.writing_title || "");
      setWritingDescription(content.writing_description || "");
      setProjectsTitle(content.projects_title || "");
      setProjectsDescription(content.projects_description || "");
      setAboutTitle(content.about_title || "");
      setAboutBio(content.about_bio || "");
    }
  }, [content]);

  // Memoize form data for autosave
  const formData = useMemo(
    () => ({
      home_tagline: homeTagline,
      home_description: homeDescription,
      home_education_line: homeEducationLine,
      writing_title: writingTitle,
      writing_description: writingDescription,
      projects_title: projectsTitle,
      projects_description: projectsDescription,
      about_title: aboutTitle,
      about_bio: aboutBio,
    }),
    [
      homeTagline,
      homeDescription,
      homeEducationLine,
      writingTitle,
      writingDescription,
      projectsTitle,
      projectsDescription,
      aboutTitle,
      aboutBio,
    ]
  );

  // Autosave callback
  const handleAutosave = useCallback(async () => {
    // Save all content fields
    const updates = [
      { key: "home_tagline", value: homeTagline },
      { key: "home_description", value: homeDescription },
      { key: "home_education_line", value: homeEducationLine },
      { key: "writing_title", value: writingTitle },
      { key: "writing_description", value: writingDescription },
      { key: "projects_title", value: projectsTitle },
      { key: "projects_description", value: projectsDescription },
      { key: "about_title", value: aboutTitle },
      { key: "about_bio", value: aboutBio },
    ];

    for (const { key, value } of updates) {
      await updateContent.mutateAsync({ key, value });
    }
  }, [
    homeTagline,
    homeDescription,
    homeEducationLine,
    writingTitle,
    writingDescription,
    projectsTitle,
    projectsDescription,
    aboutTitle,
    aboutBio,
    updateContent,
  ]);

  // Autosave hook
  const { lastSaved, isSaving, hasUnsavedChanges, saveNow, markAsSaved } = useAutosave({
    data: formData,
    onSave: handleAutosave,
    enabled: !isLoading && !!content,
  });

  // Mark as saved when content loads
  useEffect(() => {
    if (content) {
      markAsSaved();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Status indicator
  const SaveStatus = () => {
    if (isSaving) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-yellow-500 animate-pulse" />
          <span className="text-[11px] text-text-muted">Saving...</span>
        </div>
      );
    }

    if (hasUnsavedChanges) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-orange-500" />
          <span className="text-[11px] text-text-muted">Unsaved changes</span>
        </div>
      );
    }

    if (lastSaved) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-green-500" />
          <span className="text-[11px] text-text-muted">Saved {formatTimeAgo(lastSaved)}</span>
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div>
        <p className="text-text-muted">Loading content...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-[24px] md:text-[28px] font-bold text-primary">Site Content</h1>
        <div className="flex items-center gap-4">
          <SaveStatus />
          <button
            onClick={saveNow}
            disabled={isSaving || !hasUnsavedChanges}
            className="px-4 py-2 bg-primary text-background text-[13px] font-bold hover:opacity-90 active:opacity-75 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Page tabs */}
      <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
        {PAGE_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "px-4 py-2 text-[13px] whitespace-nowrap transition-colors",
              activeTab === tab.id
                ? "text-primary border-b-2 border-primary"
                : "text-text-muted hover:text-text-secondary"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="max-w-2xl">
        {/* Home Page Content */}
        {activeTab === "home" && (
          <div className="space-y-6">
            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Tagline
              </label>
              <input
                type="text"
                value={homeTagline}
                onChange={(e) => setHomeTagline(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[15px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                placeholder="e.g., Trying to be useful."
              />
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Description
              </label>
              <div className="border border-border">
                <RichTextEditor
                  content={homeDescription}
                  onChange={setHomeDescription}
                  placeholder="Brief introduction about yourself..."
                />
              </div>
              <p className="text-[11px] text-text-faint mt-1">
                Use the editor to format text with bold, italic, and links.
              </p>
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Education Line
              </label>
              <input
                type="text"
                value={homeEducationLine}
                onChange={(e) => setHomeEducationLine(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[13px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                placeholder="e.g., Economics & Physics @ Cal, '23."
              />
            </div>
          </div>
        )}

        {/* Writing Page Content */}
        {activeTab === "writing" && (
          <div className="space-y-6">
            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Page Title
              </label>
              <input
                type="text"
                value={writingTitle}
                onChange={(e) => setWritingTitle(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[15px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                placeholder="e.g., Writing"
              />
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Description
              </label>
              <div className="border border-border">
                <RichTextEditor
                  content={writingDescription}
                  onChange={setWritingDescription}
                  placeholder="Brief description of your writing..."
                />
              </div>
              <p className="text-[11px] text-text-faint mt-1">
                Use the editor to format text with bold, italic, and links.
              </p>
            </div>
          </div>
        )}

        {/* Projects Page Content */}
        {activeTab === "projects" && (
          <div className="space-y-6">
            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Page Title
              </label>
              <input
                type="text"
                value={projectsTitle}
                onChange={(e) => setProjectsTitle(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[15px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                placeholder="e.g., Projects"
              />
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Description
              </label>
              <div className="border border-border">
                <RichTextEditor
                  content={projectsDescription}
                  onChange={setProjectsDescription}
                  placeholder="Brief description of your projects..."
                />
              </div>
              <p className="text-[11px] text-text-faint mt-1">
                Use the editor to format text with bold, italic, and links.
              </p>
            </div>
          </div>
        )}

        {/* About Page Content */}
        {activeTab === "about" && (
          <div className="space-y-6">
            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Name / Title
              </label>
              <input
                type="text"
                value={aboutTitle}
                onChange={(e) => setAboutTitle(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border text-text-primary text-[15px] focus:outline-none focus:border-border-hover hover:border-border-subtle transition-colors"
                placeholder="e.g., Maxim Kraft"
              />
            </div>

            <div>
              <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
                Bio
              </label>
              <div className="border border-border">
                <RichTextEditor
                  content={aboutBio}
                  onChange={setAboutBio}
                  placeholder="Write your bio..."
                />
              </div>
              <p className="text-[11px] text-text-faint mt-1">
                Use the rich text editor to format your bio with paragraphs, links, and styling.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
